import type { ProjectRiskListItem } from "@/modules/management/project/domain/types";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FormRoot,
  FormTextarea,
  RHF,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  toast,
  zodResolver,
} from "@devhop/ui";
import { useEffect } from "react";
import { z } from "zod";
import { managementProjectApi } from "../api/client";

const RiskFormSchema = z.object({
  description: z.string().min(1, "ກະລຸນາປ້ອນລາຍລະອຽດ"),
  severity: z.enum(["high", "medium", "low"]),
  status: z.enum(["open", "mitigated", "closed"]).default("open"),
});

type RiskFormValues = z.infer<typeof RiskFormSchema>;

type RiskFormDialogProps = {
  projectId: string;
  risk?: ProjectRiskListItem | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
};

export function RiskFormDialog({
  projectId,
  risk,
  open,
  onOpenChange,
  onSuccess,
}: RiskFormDialogProps) {
  const isEdit = Boolean(risk?.id);

  const methods = RHF.useForm<RiskFormValues>({
    defaultValues: {
      description: "",
      severity: "medium",
      status: "open",
    },
    resolver: zodResolver(RiskFormSchema),
  });

  useEffect(() => {
    if (open && risk) {
      methods.reset({
        description: risk.description,
        severity: risk.severity,
        status: risk.status,
      });
    } else if (open && !risk) {
      methods.reset({
        description: "",
        severity: "medium",
        status: "open",
      });
    }
  }, [open, risk, methods]);

  const onSubmit = async (values: RiskFormValues) => {
    try {
      if (isEdit && risk) {
        await managementProjectApi.updateRisk(projectId, risk.id, values);
        toast.success("ບັນທຶກຄວາມສຽງສຳເລັດ");
      } else {
        await managementProjectApi.createRisk(projectId, values);
        toast.success("ສ້າງຄວາມສຽງສຳເລັດ");
      }
      onSuccess();
      onOpenChange(false);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "ເກີດຂໍ້ຜິດພາດ");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isEdit ? "ແກ້ໄຂຄວາມສຽງ" : "ເພີ່ມຄວາມສຽງ"}</DialogTitle>
        </DialogHeader>
        <FormRoot<RiskFormValues>
          methods={methods}
          onSubmit={onSubmit}
          className="space-y-4"
        >
          <FormTextarea
            name="description"
            label="ລາຍລະອຽດ"
            placeholder="ອະທິບາຍຄວາມສຽງ..."
            rows={3}
            requiredMark
          />
          <div className="grid grid-cols-2 gap-4">
            <Field name="severity" label="ລະດັບ">
              <RHF.Controller
                control={methods.control}
                name="severity"
                render={({ field: f }) => (
                  <Select
                    value={f.value}
                    onValueChange={(v) =>
                      f.onChange(v as "high" | "medium" | "low")
                    }
                  >
                    <SelectTrigger className="h-10 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="high">ສູງ</SelectItem>
                      <SelectItem value="medium">ກາງ</SelectItem>
                      <SelectItem value="low">ຕ່ຳ</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
            <Field name="status" label="ສະຖານະ">
              <RHF.Controller
                control={methods.control}
                name="status"
                render={({ field: f }) => (
                  <Select
                    value={f.value}
                    onValueChange={(v) =>
                      f.onChange(v as "open" | "mitigated" | "closed")
                    }
                  >
                    <SelectTrigger className="h-10 w-full">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="open">ເປີດ</SelectItem>
                      <SelectItem value="mitigated">ຫຼຸດຜ່ອນແລ້ວ</SelectItem>
                      <SelectItem value="closed">ປິດ</SelectItem>
                    </SelectContent>
                  </Select>
                )}
              />
            </Field>
          </div>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              ຍົກເລີກ
            </Button>
            <Button type="submit">{isEdit ? "ບັນທຶກ" : "ສ້າງຄວາມສຽງ"}</Button>
          </DialogFooter>
        </FormRoot>
      </DialogContent>
    </Dialog>
  );
}
